/**
 * Check if AdSense script is loaded correctly in the DOM
 * 
 * Run in console: checkAdSenseScript()
 */

export const checkAdSenseScript = () => {
  console.group('🔍 AdSense Script Check');

  // Expected script URL
  const expectedPublisherId = 'ca-pub-5882867309114563';
  const expectedScriptUrl = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${expectedPublisherId}`;

  // Check if script exists in DOM
  const script = document.querySelector('script[src*="pagead2.googlesyndication.com"]') as HTMLScriptElement;
  
  if (!script) {
    console.error('❌ AdSense script NOT found in DOM');
    console.log('💡 The script should be loaded by AdSenseLoader component');
    console.log('💡 Check if AdSenseLoader is rendered in your app');
    console.groupEnd();
    return {
      found: false,
      message: 'Script not found in DOM'
    };
  }

  console.log('✅ AdSense script found in DOM');
  console.log('Script element:', script);

  // Check script attributes
  const checks: {
    check: string;
    status: '✅' | '❌' | '⚠️';
    value: string;
    expected?: string;
  }[] = [];

  // Check src
  const src = script.src;
  checks.push({
    check: 'Script src',
    status: src.includes(expectedPublisherId) ? '✅' : '❌',
    value: src,
    expected: expectedScriptUrl
  });
  console.log('📄 Script src:', src);

  // Check async
  const isAsync = script.async;
  checks.push({
    check: 'async attribute',
    status: isAsync ? '✅' : '⚠️',
    value: isAsync ? 'true' : 'false',
    expected: 'true'
  });
  console.log('⚡ async:', isAsync);

  // Check defer
  const hasDefer = script.hasAttribute('defer');
  checks.push({
    check: 'defer attribute',
    status: hasDefer ? '✅' : '⚠️',
    value: hasDefer ? 'true' : 'false',
    expected: 'true (optional)'
  });
  console.log('⏳ defer:', hasDefer);

  // Check crossorigin
  const crossOrigin = script.crossOrigin;
  checks.push({
    check: 'crossorigin attribute',
    status: crossOrigin === 'anonymous' ? '✅' : '⚠️',
    value: crossOrigin || 'not set',
    expected: 'anonymous'
  });
  console.log('🔐 crossorigin:', crossOrigin || 'not set');

  // Check data-ad-client
  const dataAdClient = script.getAttribute('data-ad-client');
  checks.push({
    check: 'data-ad-client attribute',
    status: dataAdClient === expectedPublisherId ? '✅' : '❌',
    value: dataAdClient || 'not set',
    expected: expectedPublisherId
  });
  console.log('🏷️ data-ad-client:', dataAdClient || 'not set');

  // Check if script is loaded
  const isLoaded = script.src && !script.onerror;
  checks.push({
    check: 'Script loaded',
    status: isLoaded ? '✅' : '❌',
    value: isLoaded ? 'loaded' : 'not loaded',
    expected: 'loaded'
  });

  // Summary
  console.group('📊 Summary');
  checks.forEach(check => {
    const icon = check.status;
    console.log(`${icon} ${check.check}: ${check.value}${check.expected ? ` (expected: ${check.expected})` : ''}`);
  });

  const allPassed = checks.every(c => c.status === '✅');
  const hasErrors = checks.some(c => c.status === '❌');

  if (allPassed) {
    console.log('✅ All checks passed! Script is correctly configured.');
  } else if (hasErrors) {
    console.error('❌ Some checks failed. Please review the configuration.');
  } else {
    console.warn('⚠️ Some checks have warnings. Review if needed.');
  }

  console.groupEnd();
  console.groupEnd();

  // Expected format
  console.group('📋 Expected Format');
  console.log('The script should look like this in the DOM:');
  console.log(`<script async src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${expectedPublisherId}" crossorigin="anonymous"></script>`);
  console.log('');
  console.log('Current script HTML:');
  console.log(script.outerHTML);
  console.groupEnd();

  return {
    found: true,
    script: {
      src,
      async: isAsync,
      defer: hasDefer,
      crossOrigin: crossOrigin || 'not set',
      dataAdClient: dataAdClient || 'not set'
    },
    checks,
    allPassed
  };
};

// Make it available globally
if (typeof window !== 'undefined') {
  (window as any).checkAdSenseScript = checkAdSenseScript;
}

